import type { AggregatedStat, Targeting, TTDCampaign } from '@feathr/blackbox';
import type { ListResponse } from '@feathr/rachis';

import type { IAudienceBreakdownReturn } from './TargetAudienceOverview.useAudienceBreakdown';
import useAudienceBreakdown from './TargetAudienceOverview.useAudienceBreakdown';

export interface IProps {
  campaign: TTDCampaign;
  targetings: ListResponse<Targeting>;
  dailyStats: AggregatedStat;
}

export interface IAudienceStatsReturn
  extends Omit<
    IAudienceBreakdownReturn,
    | 'numDevicesPerPerson'
    | 'numNewDevices'
    | 'lookalikeAudienceSize'
    | 'numActualMeta'
    | 'numActualMetaLookalike'
  > {
  numReachedUsers: number;
  numReachedEmails: number;
  numReachedLookalike: number;
  numReachedCookies: number;
  numReachedDevices: number;
  numDevicesPerPerson: number;
  numNewDevices: number;
  numReachedMeta: number;
  numReachedMetaLookalike: number;
  hasGroupedStats: boolean;
}

export interface IUseAudienceStats extends IAudienceStatsReturn {
  context: 'report';
  isEmpty: boolean;
  isLoading: boolean;
}

function useAudienceStats({
  campaign,
  targetings,
  dailyStats,
}: IProps): Readonly<IUseAudienceStats> {
  const audienceData = useAudienceBreakdown({ campaign, targetings });

  const {
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    targetedLookalikeAudienceSize,
    actualLookalikeAudienceSize,
    numTargetedReachableCookies,
    numTargetedReachableDevices,
    numTargetedMeta,
    numTargetedMetaLookalike,
    numActualMeta,
    numActualMetaLookalike,
    isLoading,
  } = audienceData;

  const devicesReached = dailyStats.get('num_devices_reached');
  const personsReached = dailyStats.get('num_persons_reached');

  const numReachedDevices = devicesReached?.total_targetable ?? 0;
  const numDevicesPerPerson = devicesReached?.avg_per_person ?? 0;
  const numNewDevices = devicesReached?.new ?? 0;

  // Type guard to check if we have the new grouped structure
  type GroupedStats = {
    main?: {
      device: number;
      email: number;
      total: number;
    };
    lookalike?: {
      device: number;
      email: number;
      total: number;
    };
    meta?: {
      total: number;
    };
    meta_lookalike?: {
      total: number;
    };
  };

  type LegacyStats = {
    device?: number;
    email?: number;
    lookalike?: number;
    total?: number;
  };

  const isGroupedStats = (data: unknown): data is GroupedStats => {
    return data !== null && typeof data === 'object' && ('main' in data || 'lookalike' in data);
  };

  const hasGroupedStats = Boolean(personsReached && isGroupedStats(personsReached));

  let numReachedCookies: number;
  let numReachedEmails: number;
  let numReachedLookalike: number;
  let numReachedUsers: number;
  let numReachedMeta: number;
  let numReachedMetaLookalike: number;

  if (personsReached && isGroupedStats(personsReached)) {
    // New grouped structure - TypeScript now knows this is GroupedStats
    const mainStats = personsReached.main;
    const lookalikeStats = personsReached.lookalike;
    const metaStats = personsReached.meta;
    const metaLookalikeStats = personsReached.meta_lookalike;

    // For legacy compatibility, combine totals
    numReachedUsers = (mainStats?.total ?? 0) + (lookalikeStats?.total ?? 0);
    numReachedEmails = (mainStats?.email ?? 0) + (lookalikeStats?.email ?? 0);
    numReachedCookies = (mainStats?.device ?? 0) + (lookalikeStats?.device ?? 0);
    numReachedLookalike = lookalikeStats?.total ?? 0;
    // Prefer breakdown endpoint's meta_actual (live from Meta API) over daily stats
    numReachedMeta = numActualMeta || (metaStats?.total ?? 0);
    numReachedMetaLookalike = numActualMetaLookalike || (metaLookalikeStats?.total ?? 0);
  } else {
    // Legacy single group structure
    const legacyStats = personsReached as LegacyStats | undefined;
    numReachedCookies = legacyStats?.device ?? 0;
    numReachedEmails = legacyStats?.email ?? 0;
    numReachedLookalike = legacyStats?.lookalike ?? 0;
    numReachedUsers = legacyStats?.total ?? 0;
    // Legacy stats have no Meta groups; use breakdown endpoint values
    numReachedMeta = numActualMeta;
    numReachedMetaLookalike = numActualMetaLookalike;
  }

  const isEmpty = numReachedUsers === 0;

  return {
    // From Breakdown
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    numTargetedReachableCookies,
    numTargetedReachableDevices,
    actualLookalikeAudienceSize,
    targetedLookalikeAudienceSize,

    // From Stats
    numReachedUsers,
    numReachedEmails,
    numReachedLookalike,
    numReachedCookies,
    numReachedDevices,
    numDevicesPerPerson,
    numNewDevices,
    numTargetedMeta,
    numTargetedMetaLookalike,
    numReachedMeta,
    numReachedMetaLookalike,

    // Other
    isEmpty,
    isLoading,
    context: 'report',
    hasGroupedStats,
  };
}

export default useAudienceStats;
